<template>
  <m-dropdown
    v-model:value="expanded"
    class="ai-chat-chat-menu"
    :title="$t('general.actions')"
    :relocate-key="renaming"
    placement="bottomRight"
  >
    <m-btn
      icon="ellipsis"
      light
      hide-border
      fab
      :small="!xs"
      :xs="xs"
      @click.stop="expanded = true"
    />
    <template #overlay>
      <m-card
        class="_menu"
        no-padding
        :list="!renaming"
      >
        <m-content
          v-if="renaming"
          :class="['_rename', $store.state.breakpoint.smAndDown ? '-mobile' : '']"
          padding-xxs
        >
          <m-text-field
            v-model:value="draftTitle"
            :placeholder="$t('aiChat.titleLabel')"
            auto-focus
            auto-select
            has-background
            @keydown.enter="submit"
          />
        </m-content>
        <template v-else>
          <m-card-item
            icon="pushpin"
            @click="onTogglePin"
          >
            {{ pinned ? $t('aiChat.unpin') : $t('aiChat.pin') }}
          </m-card-item>
          <m-card-item
            icon="edit"
            @click="startRename"
          >
            {{ $t('general.rename') }}
          </m-card-item>
          <m-card-item
            icon="delete"
            @click="remove"
          >
            {{ $t('general.delete') }}
          </m-card-item>
        </template>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import useAiChatPin from '@/composables/ai-chat/ai-chat-pin';
import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import useConfirmDialog from '@/composables/confirm-dialog';
import useDebounce from 'shared/composables/debounce';
import useSnackBar from '@/composables/snackbar';
import { computed, ref, watch } from 'vue';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const RENAME_DEBOUNCE = 800;

const props = defineProps({
  chatId: {
    type: Number,
    required: true,
  },
  xs: {
    type: Boolean,
    default: false,
  },
});

const expanded = defineModel('expanded', { type: Boolean, default: false });

const { t } = useI18n();
const snackbar = useSnackBar();
const confirmDialog = useConfirmDialog();
const { debounce, cancelAll, flushAll } = useDebounce({ onBeforeUnmountFlush: true });
const { selectChat, updateChat, deleteChat } = useAiChatRepo();

const renaming = ref(false);
const draftTitle = ref('');

const chat = computed(() => selectChat(props.chatId));

const { pinned, toggle } = useAiChatPin(chat);

const onTogglePin = () => {
  expanded.value = false;
  toggle();
};

const startRename = () => {
  draftTitle.value = chat.value?.title ?? '';
  renaming.value = true;
};

const rename = () => {
  const title = draftTitle.value.trim();
  if (title === '' || title === chat.value?.title) {
    return;
  }
  updateChat({ uid: props.chatId, title })
    .catch(logCatch(() => { snackbar.error(); }));
};

const submit = () => {
  expanded.value = false;
  cancelAll();
  rename();
};

const remove = () => {
  expanded.value = false;
  confirmDialog.confirm({
    title: t('aiChat.deletePrompt'),
    onOk() {
      deleteChat({ uid: props.chatId })
        .catch(logCatch(() => { snackbar.error(); }));
    },
  });
};

watch(expanded, (shown) => {
  if (!shown) {
    flushAll();
  }
  renaming.value = false;
});

watch(draftTitle, () => debounce(rename, RENAME_DEBOUNCE));
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  ._menu {
    min-width: 15rem;

    ._rename {
      width: 24rem;

      &.-mobile {
        width: 100%;
      }
    }
  }
</style>
